import { useMemo } from 'react';
import type { NotificationItem } from '@/lib/types';
import { Bell } from 'lucide-react';

interface GroupedNotificationListProps {
  notifications: NotificationItem[];
  hasActiveFilters?: boolean;
  onSelectNotification: (id: string) => void;
  renderNotificationCard: (notification: NotificationItem) => React.ReactNode;
}

interface DateGroup {
  date: string;
  displayDate: string;
  notifications: NotificationItem[];
}

export function GroupedNotificationList({
  notifications,
  hasActiveFilters = false,
  onSelectNotification: _onSelectNotification,
  renderNotificationCard,
}: GroupedNotificationListProps) {

  // Group notifications by date
  const dateGroups = useMemo<DateGroup[]>(() => {
    const groups: Record<string, DateGroup> = {};

    notifications.forEach((notification) => {
      const date = new Date(notification.created_at);
      const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

      if (!groups[dateKey]) {
        groups[dateKey] = {
          date: dateKey,
          displayDate: formatDateHeader(date),
          notifications: [],
        };
      }

      groups[dateKey].notifications.push(notification);
    });

    // Sort by date descending (newest first)
    return Object.values(groups).sort((a, b) =>
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [notifications]);

  if (dateGroups.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Bell className="h-12 w-12 text-gray-500 mb-4" strokeWidth={1} />
          <h3 className="text-lg font-semibold text-gray-500 mb-2">
            {hasActiveFilters ? 'No notifications match your filters' : 'No Notifications'}
          </h3>
          <p className="text-gray-500">
            {hasActiveFilters ? 'Try adjusting your filter criteria' : "You're all caught up!"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      {dateGroups.map((dateGroup, index) => (
        <div key={dateGroup.date} className={index < dateGroups.length - 1 ? "mb-6" : ""}>
          {/* Date Header - matches table header styling */}
          <div className="sticky top-0 bg-gray-50 border-b px-6 py-3 z-10">
            <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wider">{dateGroup.displayDate}</h3>
          </div>

          {/* Notifications for this date */}
          <div className="divide-y">
            {dateGroup.notifications.map((notification) => (
              <div key={notification.id}>
                {renderNotificationCard(notification)}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function formatDateHeader(date: Date): string {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  // Reset hours for comparison
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Today';
  } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Yesterday';
  } else {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  }
}
